import React, { useCallback, useEffect, useState } from "react";
import { theme, card, btnPrimary, btnSecondary, inputStyle } from "../theme";
import { api, apiErrorMessage, ApiError } from "../api/client";

// Sign-in policy (backend/src/lib/signInPolicy.ts): how the people of this organisation,
// consultancy or bureau sign in. One panel serves all three — the API answers for the
// caller's own holder only (GET /sign-in-policy), so there is nothing to choose here.

type Provider = "entra_id" | "google_workspace" | "saml";

interface Policy {
  ssoRequired: boolean;
  providers: Provider[];
  entraTenantIds: string[];
  googleDomains: string[];
}

interface Member {
  id: string;
  displayName: string;
  email: string;
  method: Provider | "password" | "none";
  tenant: string | null;
  allowed: boolean | null;
}

interface PanelData {
  holder: { kind: "organization" | "consultancy" | "bureau"; name: string };
  policy: Policy;
  sessionHours: number;
  members: Member[];
}

const PROVIDER_LABEL: Record<Provider, string> = {
  entra_id: "Microsoft 365 (Entra ID)",
  google_workspace: "Google Workspace",
  saml: "SAML (your own identity provider)",
};

const METHOD_LABEL: Record<Member["method"], string> = {
  entra_id: "Microsoft",
  google_workspace: "Google",
  saml: "SAML",
  password: "Password + authenticator",
  none: "Invited to single sign-on",
};

const HOLDER_NOUN = { organization: "organisation", consultancy: "consultancy", bureau: "bureau" } as const;

function splitList(text: string): string[] {
  return text.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
}

export function SignInPolicyPanel() {
  const [data, setData] = useState<PanelData | null>(null);
  const [draft, setDraft] = useState<Policy | null>(null);
  const [tenants, setTenants] = useState("");
  const [domains, setDomains] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [lockout, setLockout] = useState<{ message: string; members: Member[] } | null>(null);

  const load = useCallback(() => {
    setError("");
    api.get<PanelData>("/sign-in-policy")
      .then((d) => {
        setData(d);
        setDraft(d.policy);
        setTenants(d.policy.entraTenantIds.join("\n"));
        setDomains(d.policy.googleDomains.join("\n"));
      })
      .catch((e) => setError(apiErrorMessage(e, "Could not load the sign-in policy.")));
  }, []);

  useEffect(load, [load]);

  async function save(confirmLockout = false) {
    if (!draft) return;
    setBusy(true); setError(""); setNotice("");
    try {
      const res = await api.put<{ policy: Policy; sessionsEnded: number }>("/sign-in-policy", {
        ssoRequired: draft.ssoRequired,
        providers: draft.providers,
        entraTenantIds: splitList(tenants),
        googleDomains: splitList(domains),
        ...(confirmLockout ? { confirmLockout: true } : {}),
      });
      setLockout(null);
      setNotice(res.sessionsEnded
        ? `Saved. ${res.sessionsEnded} member${res.sessionsEnded === 1 ? "" : "s"} signed out.`
        : "Saved.");
      load();
    } catch (e) {
      if (e instanceof ApiError && e.code === "members_locked_out") {
        const payload = e.payload as { message?: string; members?: Member[] };
        setLockout({ message: payload.message ?? "Some members could no longer sign in.", members: payload.members ?? [] });
      } else {
        setError(apiErrorMessage(e, "Could not save the sign-in policy."));
      }
    } finally {
      setBusy(false);
    }
  }

  async function moveToSso(member: Member) {
    if (!window.confirm(`${member.displayName} will be signed out, their password and authenticator app removed, and an invitation sent to ${member.email} to sign in with Microsoft or Google. Continue?`)) return;
    setBusy(true); setError(""); setNotice("");
    try {
      await api.post(`/sign-in-policy/members/${member.id}/move-to-sso`, {});
      setNotice(`Invitation sent to ${member.email}.`);
      setLockout(null);
      load();
    } catch (e) {
      setError(apiErrorMessage(e, "Could not move this member to single sign-on."));
    } finally {
      setBusy(false);
    }
  }

  if (error && !data) return <div style={{ ...card, padding: 20, color: theme.color.dangerText, fontSize: 13 }}>{error}</div>;
  if (!data || !draft) return <div style={{ ...card, padding: 20, color: theme.color.subtle, fontSize: 13 }}>Loading sign-in policy…</div>;

  const noun = HOLDER_NOUN[data.holder.kind];
  const toggleProvider = (p: Provider) => setDraft({
    ...draft,
    providers: draft.providers.includes(p) ? draft.providers.filter((x) => x !== p) : [...draft.providers, p],
  });
  const label: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: theme.color.text, display: "block", marginBottom: 6 };
  const help: React.CSSProperties = { fontSize: 12, color: theme.color.subtle, lineHeight: 1.5, margin: "4px 0 0" };
  const cell: React.CSSProperties = { padding: "8px 10px", borderBottom: `1px solid ${theme.color.border}`, fontSize: 13, color: theme.color.text, verticalAlign: "top" };
  const passwordMembers = data.members.filter((m) => m.method === "password");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ ...card, padding: 20 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: theme.color.heading }}>Sign-in policy</h3>
        <p style={{ ...help, margin: "0 0 16px" }}>
          How the people of this {noun} sign in. Only your own administrators can change it. Single sign-on
          sessions last {data.sessionHours} hours from sign-in and are re-checked against this policy whenever they renew,
          so someone who leaves loses access when their work account is disabled.
        </p>

        <label style={{ display: "flex", gap: 10, alignItems: "flex-start", fontSize: 13, color: theme.color.text, marginBottom: 16, cursor: "pointer" }}>
          <input type="checkbox" checked={draft.ssoRequired} onChange={(e) => setDraft({ ...draft, ssoRequired: e.target.checked })} style={{ marginTop: 2 }} />
          <span>
            <strong>Require single sign-on</strong>
            <span style={{ display: "block", ...help }}>
              Recommended. Passwords are refused, and so are personal Microsoft and Google accounts: everyone signs in with
              their work account. Turn this off only if the {noun} has neither Microsoft 365 nor Google Workspace; passwords
              then always need an authenticator app.
            </span>
          </span>
        </label>

        <span style={label}>Single sign-on providers allowed</span>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 16 }}>
          {(Object.keys(PROVIDER_LABEL) as Provider[])
            .filter((p) => p !== "saml" || data.holder.kind === "organization")
            .map((p) => (
              <label key={p} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: theme.color.text, cursor: "pointer" }}>
                <input type="checkbox" checked={draft.providers.includes(p)} onChange={() => toggleProvider(p)} />
                {PROVIDER_LABEL[p]}
              </label>
            ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          <div>
            <label style={label} htmlFor="sip-tenants">Approved Microsoft tenants</label>
            <textarea id="sip-tenants" value={tenants} onChange={(e) => setTenants(e.target.value)} rows={3}
              placeholder="Tenant ID, one per line" style={{ ...inputStyle, fontFamily: "var(--font-mono, monospace)", fontSize: 12 }} />
            <p style={help}>
              Empty: the first work account to sign in records its tenant, and every other tenant is refused afterwards.
              Find yours in the Entra admin centre under Overview, Tenant ID.
            </p>
          </div>
          <div>
            <label style={label} htmlFor="sip-domains">Approved Google Workspace domains</label>
            <textarea id="sip-domains" value={domains} onChange={(e) => setDomains(e.target.value)} rows={3}
              placeholder="example.com, one per line" style={{ ...inputStyle, fontSize: 12 }} />
            <p style={help}>
              Empty: recorded from the first Workspace sign-in in the same way. Google Workspace 2-Step Verification
              is set in your Google admin console.
            </p>
          </div>
        </div>

        <p style={{ ...help, marginTop: 12 }}>
          Multi-factor authentication for Microsoft accounts is enforced by your own tenant: require it with a Conditional
          Access policy (or security defaults) in Entra ID.
        </p>

        {error && <p style={{ color: theme.color.dangerText, fontSize: 13, margin: "12px 0 0" }}>{error}</p>}
        {notice && <p style={{ color: theme.color.successText, fontSize: 13, margin: "12px 0 0" }}>{notice}</p>}

        {lockout && (
          <div style={{ marginTop: 12, padding: 12, background: theme.color.warningBg, border: `1px solid ${theme.color.warning}`, borderRadius: theme.radius.sm }}>
            <p style={{ margin: "0 0 8px", fontSize: 13, color: theme.color.warningText }}>{lockout.message}</p>
            <ul style={{ margin: "0 0 10px", paddingLeft: 18, fontSize: 13, color: theme.color.text }}>
              {lockout.members.map((m) => <li key={m.id}>{m.displayName} ({m.email}) - {METHOD_LABEL[m.method]}</li>)}
            </ul>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button type="button" disabled={busy} onClick={() => save(true)} style={btnPrimary}>Save and sign them out</button>
              <button type="button" disabled={busy} onClick={() => setLockout(null)} style={btnSecondary}>Cancel</button>
            </div>
          </div>
        )}

        {!lockout && (
          <div style={{ marginTop: 16 }}>
            <button type="button" disabled={busy} onClick={() => save(false)} style={btnPrimary}>{busy ? "Saving…" : "Save policy"}</button>
          </div>
        )}
      </div>

      <div style={{ ...card, padding: 20 }}>
        <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: theme.color.heading }}>How members sign in</h3>
        <p style={{ ...help, margin: "0 0 12px" }}>
          {passwordMembers.length
            ? "Members on a password can be moved to single sign-on: they are signed out and invited to sign in again with their work account, keeping their role and history."
            : "Everyone signs in with single sign-on."}
        </p>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                {["Member", "Signs in with", "Under this policy", ""].map((h) => (
                  <th key={h} style={{ ...cell, fontSize: 11, fontWeight: 600, color: theme.color.muted, textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.id}>
                  <td style={cell}>
                    <div style={{ fontWeight: 600 }}>{m.displayName}</div>
                    <div style={{ fontSize: 12, color: theme.color.subtle }}>{m.email}</div>
                  </td>
                  <td style={cell}>
                    {METHOD_LABEL[m.method]}
                    {m.tenant && <div style={{ fontSize: 11, color: theme.color.subtle, wordBreak: "break-all" }}>{m.tenant}</div>}
                  </td>
                  <td style={{ ...cell, color: m.allowed === false ? theme.color.dangerText : m.allowed === null ? theme.color.subtle : theme.color.successText }}>
                    {m.allowed === false ? "Refused" : m.allowed === null ? "Checked at next sign-in" : "Allowed"}
                  </td>
                  <td style={{ ...cell, textAlign: "right" }}>
                    {m.method === "password" && (
                      <button type="button" disabled={busy} onClick={() => moveToSso(m)} style={{ ...btnSecondary, whiteSpace: "nowrap" }}>Move to single sign-on</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
